import React from 'react';
import '@/styles/project-plate.css';
import '@/styles/projects-plate.css';

/**
 * DP32 B.2 — a project as a PLATE: the image in a 4:3 frame with a vignette and
 * a fine grain, the number plate in the top-left corner in the project's own
 * colour, and the wall label BELOW the frame, never on it.
 *
 * DP104 Part 3 — THE MOCKUP'S MARKUP, PASTED. The classes and every value come
 * from styles/projects-plate.css, which is Chad's mockup stylesheet copied byte
 * for byte (md5 975be6fb401bfe81f6c26498ee6c6a4f). Nothing in it was retyped,
 * renamed or resized. What that brings the plate: `.vg` the vignette, `.gr` the
 * grain, `.flag` the overdue mark, `.cap`'s hairline in ink, the hover lift with
 * the image scaling to 1.04, and the caption as three tight lines.
 *
 * KEPT, by ruling: the number in the project's own colour (`.no2`, which takes
 * `--pc`), the progress rule (`.bar`), and the action buttons — Add photo,
 * Archive, Restore, Open project. The actions are live and are not in the
 * mockup; they are reported, not removed.
 *
 * DP41 — the catalogue is a NAVIGATOR. Where `onSelect` is given, one click
 * selects the plate and the plan and steps below redraw for it; a double click
 * opens the project, and an explicit Open control on the label does the same on
 * touch, where no double click exists.
 */
export const ProjectPlate = ({
  project,
  phaseLabel,
  progress = 0,
  onOpen,
  onSelect,
  selected = false,
  onArchive,
  onRestore,
  onAddPhoto,
  canEditPhotos = false,
  overdueCount = 0,
}) => {
  const colour = /^#[0-9A-Fa-f]{6}$/.test(project.brand_color || '') ? project.brand_color : null;
  const image = project.primary_image_thumbnail || project.primary_image || project.placeholder_image_thumbnail || project.placeholder_image
    || (Array.isArray(project.images) && (project.images[0]?.thumbnail_url || project.images[0]?.url));
  const client = project.client?.name || project.client_name || 'No client';
  const city = project.site_address?.city;
  const state = project.site_address?.state;
  const where = [city, state].filter(Boolean).join(', ');
  const isArchived = project.status === 'archived';
  const act = (fn) => (e) => { e.stopPropagation(); fn?.(project); };
  const open = () => (onOpen ? onOpen(project) : (window.location.href = `/projects/${project.id}/hub`));
  const pct = Math.max(0, Math.min(100, progress));
  // DP104 Part 3d — overdue comes from the project's own overdue TASKS, the same
  // source band A's Overdue tally reads (a due date in the past, not
  // completed). There is no project-level due date and the indicators endpoint
  // carries no overdue field, so this is the only source there is.
  const isOverdue = overdueCount > 0;

  return (
    <div
      className="plate km-plate"
      style={{ '--pc': colour || 'var(--km-ink-3)', ...(colour ? { '--km-plate-no': colour } : {}) }}
      onClick={() => (onSelect ? onSelect(project) : open())}
      onDoubleClick={onSelect ? () => open() : undefined}
      data-selected={selected ? 'true' : undefined}
      data-testid={`project-plate-${project.id}`}
      data-plate-colour={colour ? 'own' : 'fallback'}
    >
      <div className="fr">
        {image
          ? <div className="im" style={{ backgroundImage: `url(${image})` }} />
          : (
            <span className="km-plate__initials" aria-hidden="true">
              {`${(project.name || '?').charAt(0)}${(client || '?').charAt(0)}`.toUpperCase()}
            </span>
          )}
        <div className="vg" />
        <div className="gr" />
        {/* DP41 — the NUMBER. `project_number` is never written by the app; the
            generator writes `project_code` (2026-001, KC-CH18-2606). The id
            fragment stays as the last resort only. */}
        <div className="no2" data-testid={`project-plate-no-${project.id}`}>
          {project.project_number || project.project_code || (project.id || '').slice(0, 6)}
        </div>
        {isOverdue && (
          <div className="flag" data-testid={`project-plate-flag-${project.id}`}>
            {overdueCount} overdue
          </div>
        )}
      </div>
      <div className="cap km-plate__label">
        <div className="nm2" data-testid={`project-plate-name-${project.id}`}>{project.name}</div>
        <div className="ln">{client}{where ? ` · ${where}` : ''}</div>
        <div className="ph3" data-testid={`project-plate-phase-${project.id}`}>
          {phaseLabel || 'Phase not set'}
          {pct > 0 ? ` · ${Math.round(pct)}%` : ''}
        </div>
        <div className="bar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>
        {onSelect && (
          <button type="button" className="km-plate__open" onClick={(e) => { e.stopPropagation(); open(); }}
                  data-testid={`project-plate-open-${project.id}`}>
            Open project
          </button>
        )}
        <div className="km-plate__acts">
          {!image && canEditPhotos && !isArchived && (
            <button type="button" className="km-plate__act" onClick={act(onAddPhoto)}
                    data-testid={`project-plate-addphoto-${project.id}`}>Add photo</button>
          )}
          {isArchived
            ? <button type="button" className="km-plate__act" onClick={act(onRestore)}
                      data-testid={`project-plate-restore-${project.id}`}>Restore</button>
            : <button type="button" className="km-plate__act" onClick={act(onArchive)}
                      data-testid={`project-plate-archive-${project.id}`}>Archive</button>}
        </div>
      </div>
    </div>
  );
};

export default ProjectPlate;
